import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Input, message, Select, Table, Popconfirm } from 'antd';
import { SettingOutlined, InfoCircleOutlined, ApiOutlined, DeleteOutlined, PlusOutlined, CodeOutlined, RobotOutlined } from '@ant-design/icons';
import { agentConfigApi } from '../services/api-services';
import './Settings.css';

// ─── Header 右侧插槽 Context ────────────────────────────────────────────────────

const HeaderActionsContext = React.createContext<{
  setActions: (node: React.ReactNode) => void;
}>({ setActions: () => {} });

const useHeaderActions = (node: React.ReactNode, deps: React.DependencyList) => {
  const { setActions } = useContext(HeaderActionsContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setActions(node); return () => setActions(null); }, deps);
};

// ─── 统一布局组件 ───────────────────────────────────────────────────────────────

/** 页面内的卡片块，标题 + 内容，风格统一 */
const SettingsCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="section-card">
    <h3 className="settings-subtitle">{title}</h3>
    {children}
  </div>
);

/** 整个设置页布局：左侧导航 + 右侧固定 header + 可滚动内容 */
interface NavItem { key: string; label: string; icon: React.ReactNode }
const SettingsLayout: React.FC<{
  navItems: NavItem[];
  activeKey: string;
  onNav: (key: string) => void;
  title: string;
  children: React.ReactNode;
}> = ({ navItems, activeKey, onNav, title, children }) => {
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);

  return (
    <HeaderActionsContext.Provider value={{ setActions: setHeaderActions }}>
      <div className="settings-page">
        <div className="settings-sidebar">
          {navItems.map(item => (
            <div
              key={item.key}
              className={`settings-nav-item ${activeKey === item.key ? 'active' : ''}`}
              onClick={() => onNav(item.key)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
        <div className="settings-main">
          <div className="settings-header">
            <h1>{title}</h1>
            {headerActions && <div className="settings-header-actions">{headerActions}</div>}
          </div>
          <div className="settings-body">
            <div className="settings-content">
              {children}
            </div>
          </div>
        </div>
      </div>
    </HeaderActionsContext.Provider>
  );
};

// ─── Tab 内容组件 ───────────────────────────────────────────────────────────────

interface LocalApp { name: string; path: string; icon?: string }

const ShortcutsTab: React.FC = () => {
  const [shortcut, setShortcut] = useState('Alt+Space');
  const [recording, setRecording] = useState(false);
  const [localApps, setLocalApps] = useState<LocalApp[]>([]);
  const isTauri = !!(window as any).__TAURI_INTERNALS__;

  useEffect(() => {
    const saved = localStorage.getItem('quick_chat_shortcut');
    if (saved) setShortcut(saved);
    const savedApps = localStorage.getItem('local_quick_apps');
    if (savedApps) { try { setLocalApps(JSON.parse(savedApps)); } catch {} }
    if (isTauri) {
      (async () => {
        const { listen } = await import('@tauri-apps/api/event');
        await listen('open-local-app', async (event: any) => {
          try { const { invoke } = await import('@tauri-apps/api/core'); await invoke('open_local_app', { path: event.payload }); } catch {}
        });
      })();
    }
  }, [isTauri]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!recording) return;
    e.preventDefault();
    const keys: string[] = [];
    if (e.metaKey || e.ctrlKey) keys.push(e.metaKey ? 'Cmd' : 'Ctrl');
    if (e.altKey) keys.push('Alt');
    if (e.shiftKey) keys.push('Shift');
    const key = e.key.toUpperCase();
    if (!['META','CONTROL','ALT','SHIFT'].includes(key)) keys.push(key === ' ' ? 'Space' : key);
    if (keys.length > 1) { setShortcut(keys.join('+')); setRecording(false); }
  };

  const handleSaveShortcut = useCallback(async () => {
    localStorage.setItem('quick_chat_shortcut', shortcut);
    if ((window as any).__TAURI_INTERNALS__) {
      try { const { invoke } = await import('@tauri-apps/api/core'); await invoke('update_shortcut', { shortcut }); message.success('快捷键已更新，重启应用后生效'); }
      catch (err) { message.error('保存失败: ' + err); }
    } else {
      message.success('保存成功');
    }
  }, [shortcut]);

  useHeaderActions(
    <Button type="primary" onClick={handleSaveShortcut}>保存</Button>,
    [handleSaveShortcut],
  );

  const handleAddLocalApp = async () => {
    if (!isTauri) { message.warning('此功能仅在桌面应用中可用'); return; }
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const selected = await open({ multiple: false, filters: [{ name: 'Applications', extensions: ['app', 'exe'] }] }) as string | { path: string } | null;
      if (selected) {
        const path = typeof selected === 'string' ? selected : selected.path;
        const name = path.split('/').pop()?.replace('.app', '').replace('.exe', '') || 'Unknown';
        const newApps = [...localApps, { name, path }];
        const { invoke } = await import('@tauri-apps/api/core');
        await invoke('save_local_apps', { apps: newApps });
        setLocalApps(newApps);
        localStorage.setItem('local_quick_apps', JSON.stringify(newApps));
        message.success('应用已添加');
      }
    } catch { message.error('添加应用失败'); }
  };

  const handleRemoveLocalApp = async (index: number) => {
    const newApps = localApps.filter((_, i) => i !== index);
    setLocalApps(newApps);
    localStorage.setItem('local_quick_apps', JSON.stringify(newApps));
    if (isTauri) { try { const { invoke } = await import('@tauri-apps/api/core'); await invoke('save_local_apps', { apps: newApps }); } catch {} }
    message.success('应用已移除');
  };

  return (
    <>
      <SettingsCard title="快速聊天快捷键">
        <Input value={shortcut} readOnly placeholder="点击录制按钮后按下快捷键" onKeyDown={handleKeyDown} style={{ marginBottom: 8 }} />
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Button type={recording ? 'primary' : 'default'} onClick={() => setRecording(!recording)}>
            {recording ? '按下快捷键...' : '录制快捷键'}
          </Button>
        </div>
        <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
          <p>• 支持组合键：Cmd/Ctrl + Alt + Shift + 字母/数字/Space</p>
          <p>• 默认快捷键：Alt+Space</p>
          <p>• 修改后需重启应用生效</p>
        </div>
      </SettingsCard>

      <SettingsCard title="快捷应用">
        <div className="quick-apps-list">
          {localApps.map((app, index) => (
            <div key={index} className="quick-app-item">
              <div className="app-icon-placeholder">📱</div>
              <span className="app-name">{app.name}</span>
              <button className="remove-btn active" onClick={() => handleRemoveLocalApp(index)}>－</button>
            </div>
          ))}
        </div>
        <div className="add-app-btn" onClick={handleAddLocalApp}>
          <span style={{ color: '#1890ff', fontSize: 15 }}>+ 添加应用</span>
        </div>
      </SettingsCard>
    </>
  );
};

// ─── 通用智能体配置 Tab ─────────────────────────────────────────────────────────
// 前端完全通用：从后端拉取配置项 → 渲染表格 → 选 Profile 时拉后端预览填表 → 保存提交回后端。
// 每个智能体的固定配置项、解析、Profile 映射、写入格式全部由后端负责。

interface KV { key: string; value: string }

interface AgentConfigSpec {
  /** 后端注册的智能体 ID */
  agentId: string;
  /** 选择框下方说明文案 */
  tip: string;
  /** 配置卡片标题（通常为配置文件路径） */
  fileTitle: string;
  /** 键列标题 */
  keyLabel: string;
  /** 新增按钮文案 */
  addLabel: string;
}

const AgentConfigTab: React.FC<{ spec: AgentConfigSpec }> = ({ spec }) => {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [activeProfile, setActiveProfile] = useState('');
  const [items, setItems] = useState<KV[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    agentConfigApi.getGlobalConfig().then((res: any) => { setProfiles(res.profiles || []); }).catch(() => {});
    agentConfigApi.getAgentConfig(spec.agentId).then((res: any) => {
      setItems(res.items || []);
      if (res.active_profile) setActiveProfile(res.active_profile);
    }).catch(() => setItems([]));
  }, [spec.agentId]);

  const handleProfileChange = async (name: string) => {
    setActiveProfile(name);
    try {
      const res: any = await agentConfigApi.previewAgentConfig(spec.agentId, name);
      setItems(res.items || []);
    } catch { message.error('加载配置失败'); }
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await agentConfigApi.saveAgentConfig(spec.agentId, items.filter(i => i.key), activeProfile || undefined);
      message.success('保存成功');
    } catch { message.error('保存失败'); } finally { setSaving(false); }
  }, [spec.agentId, items, activeProfile]);

  useHeaderActions(
    <Button type="primary" loading={saving} onClick={handleSave}>保存</Button>,
    [saving, handleSave],
  );

  const columns = [
    { title: spec.keyLabel, dataIndex: 'key', width: '40%',
      render: (_: string, __: any, i: number) => (
        <Input value={items[i]?.key} onChange={e => { const n = [...items]; n[i] = { ...n[i], key: e.target.value }; setItems(n); }} style={{ fontFamily: 'monospace' }} />
      )},
    { title: '值', dataIndex: 'value',
      render: (_: string, __: any, i: number) => (
        <Input value={items[i]?.value} onChange={e => { const n = [...items]; n[i] = { ...n[i], value: e.target.value }; setItems(n); }} style={{ fontFamily: 'monospace' }} />
      )},
    { title: '', width: 48,
      render: (_: any, __: any, i: number) => (
        <Popconfirm title="确认删除？" onConfirm={() => setItems(items.filter((_, idx) => idx !== i))}>
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      )},
  ];

  return (
    <>
      <SettingsCard title="选择配置">
        <Select
          style={{ width: '100%', marginBottom: 8 }}
          placeholder="选择一个大模型 Profile 快速同步"
          value={activeProfile || undefined}
          onChange={handleProfileChange}
          options={profiles.filter((p: any) => !['acp_kiro', 'acp_copilot', 'acp_claude'].includes(p.provider)).map((p: any) => ({ value: p.name, label: p.name }))}
        />
        <p className="settings-text-tertiary" style={{ fontSize: 12 }}>{spec.tip}</p>
      </SettingsCard>

      <SettingsCard title={spec.fileTitle}>
        <Table dataSource={items} columns={columns} rowKey={(_, i) => String(i)} pagination={false} size="small" />
        <Button icon={<PlusOutlined />} size="small" style={{ marginTop: 8 }} onClick={() => setItems([...items, { key: '', value: '' }])}>{spec.addLabel}</Button>
      </SettingsCard>
    </>
  );
};

// ─── 各智能体的前端展示元数据（行为/格式由后端负责） ──────────────────────────────

const claudeCodeSpec: AgentConfigSpec = {
  agentId: 'claude_code',
  tip: '选择后自动将 base_url、token、model 写入 ~/.claude/settings.json 的 env 字段',
  fileTitle: '~/.claude/settings.json env',
  keyLabel: '变量名',
  addLabel: '添加变量',
};

const codexSpec: AgentConfigSpec = {
  agentId: 'codex',
  tip: '选择后将 base_url、model 写入 ~/.codex/config.toml，API Key 写入 ~/.codex/auth.json',
  fileTitle: '~/.codex/config.toml',
  keyLabel: '配置项',
  addLabel: '添加配置',
};

const hermesSpec: AgentConfigSpec = {
  agentId: 'hermes',
  tip: '选择后将 base_url、api_key、default model 写入 ~/.hermes/config.yaml model 块，token 写入 ~/.hermes/.env',
  fileTitle: '~/.hermes/config.yaml',
  keyLabel: '配置项',
  addLabel: '添加配置',
};

const AboutTab: React.FC = () => (
  <SettingsCard title="关于">
    <p style={{ fontSize: 15, marginBottom: 8 }}><strong>uzyClaw</strong></p>
    <p className="settings-text-secondary" style={{ fontSize: 14, marginBottom: 8 }}>版本：1.0.0</p>
    <p className="settings-text-tertiary" style={{ fontSize: 14 }}>通用智能体编排平台</p>
  </SettingsCard>
);

// ─── Tab 注册表：新增 Tab 只需在此加一条 ────────────────────────────────────────

const NAV_ITEMS: NavItem[] = [
  { key: 'shortcuts', label: '快捷操作', icon: <SettingOutlined /> },
  { key: 'model',     label: 'Claude Code 配置', icon: <ApiOutlined /> },
  { key: 'codex',     label: 'Codex 配置', icon: <CodeOutlined /> },
  { key: 'hermes',    label: 'Hermes 配置', icon: <RobotOutlined /> },
  { key: 'about',     label: '关于',     icon: <InfoCircleOutlined /> },
];

const renderTabContent = (key: string): React.ReactNode => {
  switch (key) {
    case 'shortcuts': return <ShortcutsTab />;
    case 'model':     return <AgentConfigTab spec={claudeCodeSpec} />;
    case 'codex':     return <AgentConfigTab spec={codexSpec} />;
    case 'hermes':    return <AgentConfigTab spec={hermesSpec} />;
    case 'about':     return <AboutTab />;
    default:          return null;
  }
};

// ─── 主组件 ────────────────────────────────────────────────────────────────────

const Settings: React.FC = () => {
  const [activeKey, setActiveKey] = useState('shortcuts');
  const current = NAV_ITEMS.find(n => n.key === activeKey)!;

  return (
    <SettingsLayout navItems={NAV_ITEMS} activeKey={activeKey} onNav={setActiveKey} title={current.label}>
      {renderTabContent(activeKey)}
    </SettingsLayout>
  );
};

export default Settings;
